/**
 * Local notification helpers for reminders and morning briefings.
 *
 * All native calls go through circadiaEngineProvider — the same singleton used
 * everywhere else in the app. On web (no native bridge) all methods are no-ops
 * because the provider supplies safe stubs.
 *
 * ID reservations
 * ───────────────
 * User reminder IDs   : assigned by the server (typically low integers, e.g. 1–999)
 * Morning briefing IDs: 900000–900006  (7 daily slots)
 *
 * ⚠️  Never use IDs 900000–900006 for user reminders. The range is high enough
 *     that no backend auto-increment will realistically reach it.
 */

import { circadiaEngineProvider } from "../AppController";

// ─── Morning briefing ID reservation ─────────────────────────────────────────

const MORNING_BRIEFING_BASE_ID = 900000;
const MORNING_BRIEFING_DAYS = 7;

/** Returns true for any ID in the reserved morning-briefing slot range. */
const isMorningBriefingId = (id: number): boolean =>
  id >= MORNING_BRIEFING_BASE_ID &&
  id < MORNING_BRIEFING_BASE_ID + MORNING_BRIEFING_DAYS;

// ─── Schedule next 7 days of morning briefings ────────────────────────────────

/**
 * Morning briefing is **force-disabled** app-wide. This function no longer
 * schedules anything; it only cancels any pending slots defensively so a
 * stray caller cannot revive the window. Kept exported to avoid breaking
 * imports if anything still references it.
 */
export async function scheduleMorningBriefing(
  _firstName: string
): Promise<void> {
  await cancelMorningBriefing();
}

// ─── Cancel all 7 morning briefing slots ─────────────────────────────────────

export async function cancelMorningBriefing(): Promise<void> {
  for (let i = 0; i < MORNING_BRIEFING_DAYS; i++) {
    try {
      await circadiaEngineProvider.cancelLocalNotification({
        id: MORNING_BRIEFING_BASE_ID + i,
      });
    } catch {
      // Ignore individual cancellation failures.
    }
  }
}

// ─── How many days remain in the current window ──────────────────────────────

/**
 * Returns the number of still-pending morning briefing notifications.
 * Used to decide whether the window needs topping up.
 */
export async function getMorningBriefingDaysRemaining(): Promise<number> {
  try {
    const { ids } = await circadiaEngineProvider.getPendingNotificationIds();
    return ids.filter(isMorningBriefingId).length;
  } catch {
    return 0;
  }
}

// ─── Top-up helper (call on every app open) ───────────────────────────────────

/**
 * Morning briefing is **force-disabled**.
 *
 * On every app open we cancel any morning briefing slots that may still be
 * pending from a previous app version (users who had it enabled will have
 * their 7 slots silently cleared). Scheduling is never performed.
 *
 * This intentionally ignores the Firestore `morning_briefing_enabled`
 * preference so that a future flag flip can't accidentally re-enable it —
 * removal should be a deliberate code change.
 *
 * To restore this feature: revert this file + re-wire `useMorningBriefingSetting`
 * and the profile toggle.
 */
export async function ensureMorningBriefingScheduled(
  _firstName: string,
  _email: string | undefined
): Promise<void> {
  if (!circadiaEngineProvider.isWindowCircadia()) return;
  try {
    await cancelMorningBriefing();
  } catch {
    // Non-critical — swallow.
  }
}

// ─── Permission ───────────────────────────────────────────────────────────────

/**
 * Request notification permission via the native bridge.
 * On web this is always a no-op that resolves to true.
 */
export async function requestReminderPermission(): Promise<boolean> {
  if (!circadiaEngineProvider.isWindowCircadia()) return true;
  try {
    const result = await circadiaEngineProvider.requestNotifPermission();
    return result?.granted ?? true;
  } catch {
    return true;
  }
}

// ─── Schedule ─────────────────────────────────────────────────────────────────

/**
 * Schedule (or reschedule) a local notification for a reminder.
 *
 * - Skips if the reminder is inactive or has no time set.
 * - Cancels any existing notification for the same id first to avoid duplicates.
 * - One-time reminders in the past are skipped silently.
 */
export async function scheduleReminderNotification(
  reminder: IReminder
): Promise<void> {
  if (reminder.is_active !== "on" || !reminder.reminder_time) return;

  const [hStr, mStr] = reminder.reminder_time.split(":");
  const hour = parseInt(hStr, 10);
  const minute = parseInt(mStr, 10);

  await cancelReminderNotification(reminder.id);

  try {
    switch (reminder.repeat) {
      case "none": {
        const dateStr =
          reminder.reminder_date ?? new Date().toISOString().split("T")[0];
        const at = new Date(
          `${dateStr}T${String(hour).padStart(2, "0")}:${String(
            minute
          ).padStart(2, "0")}:00`
        );
        if (at <= new Date()) return;
        await circadiaEngineProvider.scheduleLocalNotification({
          id: reminder.id,
          title: "Reminder",
          body: reminder.title,
          scheduleType: "once",
          hour,
          minute,
          dateMs: at.getTime(),
          openRoute: "/overview",
        });
        break;
      }
      case "daily": {
        await circadiaEngineProvider.scheduleLocalNotification({
          id: reminder.id,
          title: "Reminder",
          body: reminder.title,
          scheduleType: "daily",
          hour,
          minute,
          openRoute: "/overview",
        });
        break;
      }
      case "weekly": {
        const refDate = reminder.reminder_date
          ? new Date(`${reminder.reminder_date}T00:00:00`)
          : new Date();
        // iOS/Android Calendar weekday convention: 1 = Sunday … 7 = Saturday
        const weekday = refDate.getDay() + 1;
        await circadiaEngineProvider.scheduleLocalNotification({
          id: reminder.id,
          title: "Reminder",
          body: reminder.title,
          scheduleType: "weekly",
          hour,
          minute,
          weekday,
          openRoute: "/overview",
        });
        break;
      }
      case "monthly": {
        const dateStr =
          reminder.reminder_date ?? new Date().toISOString().split("T")[0];
        const day = parseInt(dateStr.split("-")[2], 10);
        await circadiaEngineProvider.scheduleLocalNotification({
          id: reminder.id,
          title: "Reminder",
          body: reminder.title,
          scheduleType: "monthly",
          hour,
          minute,
          day,
          openRoute: "/overview",
        });
        break;
      }
    }
  } catch {
    // Never let a notification error interrupt the reminder save flow.
  }
}

// ─── Cancel ───────────────────────────────────────────────────────────────────

/**
 * Cancel the local notification for a reminder.
 * Safe to call even if no notification exists for the given id.
 */
export async function cancelReminderNotification(
  reminderId: number
): Promise<void> {
  try {
    await circadiaEngineProvider.cancelLocalNotification({ id: reminderId });
  } catch {
    // Ignore.
  }
}

// ─── Sync ─────────────────────────────────────────────────────────────────────

/**
 * Reconcile all scheduled local notifications with the server reminder list.
 *
 * Called once after fetchReminders so that:
 *  - Deleted reminders have their notifications cancelled.
 *  - Active reminders that lost their notification (reinstall, etc.) are rescheduled.
 */
export async function syncRemindersToNotifications(
  reminders: IReminder[]
): Promise<void> {
  try {
    const { ids } = await circadiaEngineProvider.getPendingNotificationIds();
    const pendingIds = new Set<number>(ids);
    const serverIds = new Set(reminders.map((r) => r.id));

    for (const pendingId of pendingIds) {
      // Never cancel morning briefing slots — they are managed separately.
      if (isMorningBriefingId(pendingId)) continue;
      if (!serverIds.has(pendingId)) {
        await cancelReminderNotification(pendingId);
      }
    }

    for (const reminder of reminders) {
      if (reminder.is_active === "on" && !pendingIds.has(reminder.id)) {
        await scheduleReminderNotification(reminder);
      }
    }
  } catch {
    // Sync failures are non-fatal.
  }
}
